import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { User, EmailAuthProvider, connectAuthEmulator, createUserWithEmailAndPassword as createUserWithEmailAndPasswordFirebase, getAuth, onAuthStateChanged, reauthenticateWithCredential, sendPasswordResetEmail as sendPasswordResetEmailFirebase, signInWithEmailAndPassword, signInAnonymously, updatePassword as updatePasswordFirebase, updateProfile, verifyBeforeUpdateEmail } from "firebase/auth";
import { CollectionReference, DocumentReference, Firestore, Query as FirestoreQuery, QueryDocumentSnapshot, WriteBatch, Timestamp as _Timestap, arrayRemove as _arrayRemove, query as _query, and, arrayUnion, collection, connectFirestoreEmulator, deleteDoc, doc, getCountFromServer, getDoc, getDocs, getFirestore, limit, onSnapshot, or, orderBy, serverTimestamp, setDoc, startAfter, updateDoc, where, writeBatch } from "firebase/firestore";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { useEffect, useState } from "react";
import { CategoryData, ContainerData, SourceData, StatementData, ArgumentData, QuestionData, _CategoryData, _ContainerData, _SourceData, _StatementData, _ArgumentData, _QuestionData } from "../dataModel";
import { computeGradationArgumentConclusionStances, convertInfoToPath, convertPathToInfo, convertToPublicElementInfo, createPathFromId, createPathWithUUID, createPublicPathFromId, getIdFromPath, getLocationFromPath, getTopics, isEmpty } from "../helperFunctions";
import { FirestoreConstants, PathConstants, dbPath, userId, elementId, ElementPath, StanceType, ContainerType, QuestionAnswerType } from "../typings/types";

const firebaseConfig = {
  apiKey: "AIzaSyCjSXrrNnbzWtvsfgboXEks0f7fzLTsGp0",
  authDomain: "test-d99d9.firebaseapp.com",
  projectId: "test-d99d9",
  storageBucket: "test-d99d9.appspot.com",
  messagingSenderId: "8815986443",
  appId: "1:8815986443:web:0776ca2a84581a6bba040e",
  measurementId: "G-W2CTG9BHS6"
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// Only set for local/CI E2E test runs (see e2e/, playwright.config.ts) -- never set in normal
// dev/prod builds, so this has no effect outside of test runs.
if (process.env.EXPO_PUBLIC_USE_FIREBASE_EMULATOR === 'true') {
  connectAuthEmulator(getAuth(firebaseApp), 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(getFirestore(firebaseApp), '127.0.0.1', 8080);
}

const analytics = getAnalytics(firebaseApp);
const functions = getFunctions(firebaseApp);

export function getUserId(): userId {
  const userId = getAuth().currentUser?.uid;
  if (userId === undefined) {
    throw new Error('User is not logged in.');
  }
  return userId;
}

export type Query = FirestoreQuery;

export type Timestamp = _Timestap;

export const ServerTimestampNow = () => serverTimestamp();
// TODO: This is a workaround for a bug in the firebase typings but would allow the user to set the timestamp to any value.
export const TimestampNow = () => _Timestap.now();

export type ServerTimestamp = typeof TimestampNow;

export const arrayRemove = _arrayRemove;

export const PublishedListQuery: () => Query = () => {
  return _query(getFeedElementsRef(),
    where('parentPaths', '==', []),
    where('userId', '==', getUserId()),
    orderBy('creationTime', 'desc'));
}

function getBookmarkedRefForUser(): CollectionReference {
  return collection(getUserDataRef(getUserId()), FirestoreConstants.BOOKMARKS);
}

export const BookmarkedListQuery: () => Query = () => {
  return _query(getBookmarkedRefForUser(), orderBy('creationTime', 'desc'));
}

export async function addBookmark(info: ElementPath): Promise<boolean> {
  return setDoc(doc(getBookmarkedRefForUser(), info.id), { creationTime: ServerTimestampNow() })
    .then(() => true)
    .catch(() => false);
}

export async function removeBookmark(info: ElementPath): Promise<boolean> {
  return deleteDoc(doc(getBookmarkedRefForUser(), info.id))
    .then(() => true)
    .catch(() => false);
}

export async function toggleBookmark(
  info: ElementPath,
): Promise<{ [key: string]: boolean }> {
  const bookmarkedRef = doc(getBookmarkedRefForUser(), info.id);
  const bookmarked = await getDoc(bookmarkedRef);
  const response = { add: false, remove: false };
  if (bookmarked.exists) {
    response.remove = await removeBookmark(info);
  } else {
    response.add = await addBookmark(info);
  }
  return response;
}

// Called once a user registered to check if the base structure of their data exists in the database.
// If it does not, it is created.
export const initializeUserData = async (userId: userId, userName: string, userEmail: string, marketingConsent: boolean) => {
  const userData = await getDoc(getUserDataRef(userId));

  if (!userData.exists()) {
    await setDoc(getUserDataRef(userId), {});

    const personalElementsRef = collection(getUserDataRef(userId), FirestoreConstants.PERSONAL_ELEMENTS);
    await setDoc(doc(personalElementsRef, FirestoreConstants.ROOT_ID), { childPaths: [] });

    setDoc(doc(collection(getUserDataRef(userId), FirestoreConstants.DATA), FirestoreConstants.PUBLIC_USER_DATA), { userName: userName });
    setDoc(doc(collection(getUserDataRef(userId), FirestoreConstants.DATA), FirestoreConstants.PRIVATE_USER_DATA), { email: userEmail, marketingConsent: marketingConsent });
  }
};

export async function saveContainerToDB(container: ContainerData, stance: StanceType, groupId?: string): Promise<void> {
  // TODO: Implement feedback for Errors.
  // TODO: prevent overriding of existing containers or at least make the container consistent and not mix properties of the old and new container. The former might require transactions instead of batch writes.
  if (container.getParentPaths().length === 0) {
    return;
  }

  const batch = writeBatch(getFirestore());

  batch.set(getRefFromDB(container.getInfo()), container.getData());

  // TODO: For now, updating childs is too complicated as it requires knowledge about the stance of each child to the new parent. Could simply be neutral but probably will not need this right now.
  // for(const childPath of container.getChildPaths()) {
  //     batch.update(getPersonalStatementsRef().doc(childPath), {parentPaths: firestore.FieldValue.arrayUnion(container.getId())});
  // }

  // TODO: Missing proper handling of public/feed saves.
  if (container.getParentInfos()[0].type !== ContainerType.GROUP) {
    for (const parentInfo of container.getParentInfos()) {
      const childPath: { [key: dbPath]: StanceType } = {};
      childPath[container.getPath()] = stance;
      batch.update(getRefFromDB(parentInfo), {
        childPaths: arrayUnion(childPath),
      });
    }
  }

  batch.commit().catch(error => {
    console.error('Error saving container to DB: ', error);
  });
}

/**
 * Hook for getting the current user, which is updated when the user changes (logged in/out, different user).
 *
 * @returns [currentUser, setCurrentUser] Pair of the current user and a function to set the current user.
 */
export function useUserState(): [User | null, boolean] {
  const [currentUser, setCurrentUser] = useState(getAuth().currentUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const subscriber = getAuth().onAuthStateChanged(user => {
      setCurrentUser(user);
      setLoading(false);
    });
    return subscriber; // unsubscribe on unmount
  }, []);

  return [currentUser, loading];
}

export function useLoggedInState(): [boolean, boolean] {
  const [userState, loading] = useUserState();
  return [userState !== null, loading];
}

export function useUserName(userId: userId = getUserId()): [string, boolean] {
  const [userName, setUserName] = useState('unknown user');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserName(userId).then(name => {
      setUserName(name);
      setLoading(false);
    }), []
  });

  return [userName, loading];
}

export function useUserEmail(): string {
  const [userEmail, setUserEmail] = useState('unknown email');

  useEffect(() => {
    getUserEMail().then(email => {
      setUserEmail(email);
    }), []
  });

  return userEmail;
}

function getUserDataRef(userId: userId = getUserId()): DocumentReference {
  return doc(getFirestore(), FirestoreConstants.USER_DATA, userId);
}

function getUserPersonalElementsRef(userId: userId = getUserId()): CollectionReference {
  return collection(getUserDataRef(userId), FirestoreConstants.PERSONAL_ELEMENTS);
}

function getPublicDataRef(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.PUBLIC_DATA);
}

function getSourcesReference(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.SOURCES);
}

function getPublicTopicsReference(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.TOPICS);
}

function getFeedRef(): DocumentReference {
  return doc(getPublicDataRef(), FirestoreConstants.FEED);
}

function getFeedElementsRef(): CollectionReference {
  return collection(getFeedRef(), FirestoreConstants.FEED_ELEMENTS);
}

function getPublicQuestionsReference(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.QUESTIONS);
}

function getGroupsReference(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.GROUPS);
}

function getGroupReference(groupId: string): DocumentReference {
  return doc(getGroupsReference(), groupId);
}

function getGroupStatementsReference(groupId: string): CollectionReference {
  return collection(getGroupReference(groupId), FirestoreConstants.STATEMENTS);
}

function getGroupQuestionsReference(groupId: string): CollectionReference {
  return collection(getGroupReference(groupId), FirestoreConstants.QUESTIONS);
}

function getGroupArgumentsReference(groupId: string): CollectionReference {
  return collection(getGroupReference(groupId), FirestoreConstants.ARGUMENTS);
}

function getGroupTopicsReference(groupId: string): CollectionReference {
  return collection(getGroupReference(groupId), FirestoreConstants.TOPICS);
}

function getGroupReferencesReference(groupId: string): CollectionReference {
  return collection(getGroupReference(groupId), FirestoreConstants.REFERENCES);
}

function getFeedRootElementsRef(): CollectionReference {
  return collection(getFeedRef(), FirestoreConstants.FEED_ROOT_ELEMENTS);
}

function getCoursesReference(): CollectionReference {
  return collection(getFirestore(), FirestoreConstants.COURSES);
}

export function getCourseReference(id: string): DocumentReference {
  return doc(getCoursesReference(), id);
}

export function getTopicReference(topicId?: string, groupId?: string): DocumentReference {
  if (groupId) {
    return doc(getGroupTopicsReference(groupId), topicId);
  } else {
    return doc(getPublicTopicsReference(), topicId);
  }
}

function getRootElementsRef(path: dbPath): CollectionReference {
  if (path === PathConstants.PUBLIC) {
    return getFeedRootElementsRef();
  } else {
    throw new Error('Invalid path: ' + path);
  }
}

function getRefFromDB(info: ElementPath): DocumentReference {
  switch (info.loc) {
    case PathConstants.PERSONAL:
      return doc(getUserPersonalElementsRef(info.userId), info.id);
    case PathConstants.PUBLIC:
      switch (info.type) {
        case ContainerType.QUESTION:
          return doc(getPublicQuestionsReference(), info.id);
        default:
          return doc(getFeedElementsRef(), info.id);
      }
    case PathConstants.GROUP:
      if (info.type === ContainerType.STATEMENT) {
        return doc(getGroupStatementsReference(info.groupId), info.id);
      } else if (info.type === ContainerType.ARGUMENT) {
        return doc(getGroupArgumentsReference(info.groupId), getIdFromPath(info.id));
      } else if (info.type === ContainerType.CATEGORY) {
        return doc(getGroupTopicsReference(info.groupId), info.id);
      } else if (info.type === ContainerType.SOURCE) {
        return doc(getGroupReferencesReference(info.groupId), getIdFromPath(info.id));
      } else if (info.type === ContainerType.QUESTION) {
        return doc(getGroupQuestionsReference(info.groupId), info.id);
      } else {
        throw new Error('Invalid type: ' + info.type);
      }
    default:
      throw new Error('Invalid element: ' + info);
  }
}

export async function getContainerDataFromDB<T extends ContainerData>(info: ElementPath): Promise<T> {
  return getDoc(getRefFromDB(info))
    .then(doc => {
      if (doc.exists) {
        return new ContainerData(doc.data() as _ContainerData) as T;
      }
      throw new Error('Document does not exist.');
    });
}

export type CommentEntry = {
  id: string;
  userId: userId;
  content: string;
  creationTime: Timestamp;
};

// Comments live in a subcollection of the commented-on container's own document, so they are
// reachable through the same ElementPath -> DocumentReference resolution every other container
// operation uses (getRefFromDB), rather than a separate top-level collection.
function getCommentsReference(info: ElementPath): CollectionReference {
  return collection(getRefFromDB(info), FirestoreConstants.COMMENTS);
}

// Who is allowed to call this is enforced by the UI (see canAddComment in helperFunctions.ts),
// not here -- this repo's firestore.rules are emulator-only and permissive (see that file), so
// there is no server-side rule to mirror.
export async function addComment(info: ElementPath, content: string, memberId?: string): Promise<void> {
  await setDoc(doc(getCommentsReference(info)), {
    userId: memberId || getUserId(),
    content: content,
    creationTime: TimestampNow(),
  });
}

export function useComments(info: ElementPath): {
  comments: CommentEntry[];
  loading: boolean;
  error: Error | null;
} {
  const [comments, setComments] = useState<CommentEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (info === undefined || isEmpty(info.id) || isEmpty(info.loc)) {
      setError(new Error('Cannot listen to comments on a container with an empty id.'));
      setLoading(false);
      return;
    }

    try {
      const commentsQuery = _query(getCommentsReference(info), orderBy('creationTime', 'asc'));
      const unsubscribe = onSnapshot(commentsQuery, snapshot => {
        setComments(
          snapshot.docs.map(commentDoc => ({ id: commentDoc.id, ...commentDoc.data() } as CommentEntry)),
        );
        setLoading(false);
      }, error => {
        setError(error);
        setLoading(false);
      });
      return () => {
        unsubscribe();
      };
    } catch (error: any) {
      setError(error);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { comments, loading, error };
}

export async function publishSubtree(info: ElementPath): Promise<void> {
  const batch = writeBatch(getFirestore());

  const rootContainer = await ContainerData.getFromDB(info);
  const childPaths = [...rootContainer.getChildPaths()];
  // ContainerData's setters (setParentPaths included) call applyUpdate() immediately and
  // write straight to the document at the container's *current* path/location, unless
  // updates are paused first. Without pausing them here, this setParentPaths([]) call -
  // which only exists to make the upcoming convertToPublicData() produce a root-level
  // public copy - fires before the path has been switched to the public one, so it
  // overwrites the still-personal original document's own parentPaths with [], severing it
  // from its parent in its original (personal) location even though only a public copy was
  // meant to be written. convertToPublicData() itself pauses updates first for exactly this
  // reason; this call needs the same guard.
  rootContainer.keepUpdatesLocal();
  rootContainer.setParentPaths([]);

  batch.set(
    getRefFromDB(convertToPublicElementInfo(info)),
    rootContainer.convertToPublicData().getData(),
  );

  // This does not terminate if there is a cycle in the tree.
  // Must be fixed in the future by checking if a path has already been visited.
  while (childPaths.length > 0) {
    const childPath = childPaths.pop();
    const childContainer = await ContainerData.getFromDB(convertPathToInfo(childPath));
    childPaths.push(...childContainer.getChildPaths());
    batch.set(
      getRefFromDB(convertToPublicElementInfo(convertPathToInfo(childPath))),
      childContainer.convertToPublicData().getData(),
    );
  }

  batch
    .commit()
    .then(() => {
      Promise.resolve();
    })
    .catch(error => {
      console.error('Error publishing subtree: ', error);
      Promise.reject(error);
    });
}

export async function getUserName(userId: userId = getUserId()): Promise<string> {
  const userDataReference = collection(getUserDataRef(userId), FirestoreConstants.DATA);
  const publicUserDataReference = doc(userDataReference, FirestoreConstants.PUBLIC_USER_DATA);
  const publicUserData = await getDoc(publicUserDataReference);
  return publicUserData.data()?.userName;
}

export async function getUserEMail(userId: userId = getUserId()): Promise<string> {
  const userDataReference = collection(getUserDataRef(userId), FirestoreConstants.DATA);
  const privateUserDataReference = doc(userDataReference, FirestoreConstants.PRIVATE_USER_DATA);
  const privateUserData = await getDoc(privateUserDataReference);
  return privateUserData.data()?.email;
}

/**
!!!
Currently accepts a collection of ids and converts them to public paths.
Therefore, it only works for public elements right now.
Also, it is customized for bookmarks, published elements and feed elements.
It likely requires refactoring to be more generic.
*/
export function useLazyElementsLoader(
  query: Query,
  initialEntries = 10,
  location: PathConstants,
  groupId?: string,
): {
  infos: ElementPath[];
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
  loadMoreElements: (numberOfElements: number) => void;
} {
  const [infos, setRootInfos] = useState([] as ElementPath[]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined as Error | undefined);

  const [lastDoc, setLastDoc] = useState(
    undefined as QueryDocumentSnapshot | undefined,
  );
  const [firstDoc, setFirstDoc] = useState(
    undefined as QueryDocumentSnapshot | undefined,
  );
  const [endReached, setEndReached] = useState(false);

  const loadElements = (numOfElements: number, reload = false) => {
    // TODO: Also needs improved reloading. Currently, it reloads the entire feed, which is not necessary.
    if (endReached && !reload) {
      return;
    }
    if (loading) {
      return;
    } else {
      setLoading(true);
    }

    if (reload) {
      setLastDoc(undefined);
      setFirstDoc(undefined);
      setEndReached(false);
      setError(undefined);
    } else if (lastDoc) {
      query = _query(query, startAfter(lastDoc));
    }

    const newInfos: ElementPath[] = [];

    getDocs(query)
      .then(querySnapshot => {
        querySnapshot.forEach(doc => {
          newInfos.push(convertPathToInfo(createPathFromId(doc.id, location), groupId));
        });
        if (newInfos.length > 0) {
          setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
          setFirstDoc(querySnapshot.docs[0]);
          if (reload) {
            setRootInfos(newInfos);
          } else {
            setRootInfos(prevInfos => [...prevInfos, ...newInfos]);
          }
        } else {
          setEndReached(true);
        }
      })
      .catch(error => {
        console.error('Error getting documents: ', error);
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const refresh = () => {
    loadElements(initialEntries, true);
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query,
      querySnapshot => {
        const newInfos: ElementPath[] = [];
        querySnapshot.forEach(doc => {
          newInfos.push(convertPathToInfo(createPathFromId(doc.id, location), groupId));
        });
        setRootInfos(newInfos);
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setFirstDoc(querySnapshot.docs[0]);
      },
      // Without this, a failing query (e.g. a composite index Firestore has not been given,
      // such as the one PublishedListQuery needs) fails silently: the list is just left empty
      // forever with nothing telling the user or a developer why.
      error => {
        console.error('Error listening to query: ', error);
        setError(error);
      },
    );
    return unsubscribe;
  }, []);

  return {
    infos: infos,
    loading,
    error,
    refresh,
    loadMoreElements: loadElements,
  };
}

// Legacy but might be useful in the future for the more generic useLazyElementsLoader.
// export function useFeedLoader(initialEntries = 10): {
//   rootPaths: dbPath[];
//   loading: boolean;
//   error: Error | undefined;
//   reloadFeed: () => void;
//   loadMoreEntries: (numberOfEntries: number) => void;
// } {
//   const [rootPaths, setRootPaths] = useState([] as uuid[]);

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(undefined as Error | undefined);

//   const [lastDoc, setLastDoc] = useState(
//     undefined as QueryDocumentSnapshot | undefined,
//   );
//   const [firstDoc, setFirstDoc] = useState(
//     undefined as QueryDocumentSnapshot | undefined,
//   );
//   const [endReached, setEndReached] = useState(false);

//   const loadEntries = async (numberOfEntries: number, reload = false) => {
//     // TODO: Will need ordering since order is not guaranteed.
//     // TODO: Also needs improved reloading. Currently, it reloads the entire feed, which is not necessary.
//     // BUG: It loads the same entries again when loading more entries. Ordering might fix this.
//     if (endReached && !reload) {
//       return;
//     }
//     if (loading) {
//       return;
//     } else {
//       setLoading(true);
//     }

//     let q = query(getFeedElementsRef(),
//       where('parentPaths', '==', []),
//       orderBy('creationTime', 'desc'),
//       limit(numberOfEntries));

//     if (reload) {
//       setLastDoc(undefined);
//       setFirstDoc(undefined);
//       setEndReached(false);
//       setError(undefined);
//     } else if (lastDoc) {
//       q = query(q, startAfter(lastDoc));
//     }

//     const newPaths: uuid[] = [];

//     try {
//       const querySnapshot = await getDocs(q);
//       // console.log('Query snapshot: ', querySnapshot);
//       querySnapshot.forEach((doc) => {
//         newPaths.push(createPublicPathFromId(doc.id));
//       });
//       if (newPaths.length > 0) {
//         setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
//         setFirstDoc(querySnapshot.docs[0]);
//         if (reload) {
//           setRootPaths(newPaths);
//         } else {
//           // console.log('Adding new paths: ', newPaths);
//           setRootPaths(prevRootPaths => [...prevRootPaths, ...newPaths]);
//         }
//       } else {
//         setEndReached(true);
//       }
//     } catch (error: any) {
//       console.log('Error getting documents: ', error);
//       setError(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const reloadFeed = () => {
//     loadEntries(initialEntries, true);
//   };

//   useEffect(() => {
//     loadEntries(initialEntries, true);
//   }, []);

//   return {
//     rootPaths: rootPaths,
//     loading,
//     error,
//     reloadFeed,
//     loadMoreEntries: loadEntries,
//   };
// }

export function signIn(mail: string, password: string): Promise<void> {
  return signInWithEmailAndPassword(getAuth(), mail, password)
    .then(() => {
      Promise.resolve();
    });
}

export async function signInAnonymousUser(): Promise<void> {
  return signInAnonymously(getAuth())
    .then(() => {
      Promise.resolve();
    });
}

export function signOut(): Promise<void> {
  return getAuth().signOut();
}

export function sendPasswordResetEmail(mail: string): Promise<void> {
  return sendPasswordResetEmailFirebase(getAuth(), mail);
}

export function createUser(mail: string, password: string, userName: string, marketingConsent: boolean): Promise<void> {
  return createUserWithEmailAndPasswordFirebase(getAuth(), mail, password)
    .then(userCredentials => {
      const p1 = updateProfile(userCredentials.user, { displayName: userName });
      const p2 = initializeUserData(userCredentials.user.uid, userName, mail, marketingConsent);
      return Promise.all([p1, p2]);
    })
    .then(() => {
      Promise.resolve();
    });
}

// Updates the user's display name (shown in Settings as "Display Name" and used as the public
// username) in both Firebase Auth's profile and the public user data doc that getUserName() reads.
export function updateUserName(newUserName: string): Promise<void> {
  const user = getAuth().currentUser;
  if (!user) {
    return Promise.reject(new Error('User is not logged in.'));
  }
  const userId = getUserId();
  const p1 = updateProfile(user, { displayName: newUserName });
  const p2 = setDoc(
    doc(collection(getUserDataRef(userId), FirestoreConstants.DATA), FirestoreConstants.PUBLIC_USER_DATA),
    { userName: newUserName },
    { merge: true },
  );
  return Promise.all([p1, p2]).then(() => undefined);
}

// Firebase requires a recently-authenticated session for sensitive account changes (password,
// email). Re-authenticating with the user's current password right before the change avoids
// surprising the user with an opaque 'auth/requires-recent-login' failure.
function reauthenticateCurrentUser(currentPassword: string): Promise<User> {
  const user = getAuth().currentUser;
  if (!user || !user.email) {
    return Promise.reject(new Error('User is not logged in.'));
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  return reauthenticateWithCredential(user, credential).then(() => user);
}

export function changeUserPassword(currentPassword: string, newPassword: string): Promise<void> {
  return reauthenticateCurrentUser(currentPassword)
    .then(user => updatePasswordFirebase(user, newPassword));
}

// Uses verifyBeforeUpdateEmail rather than the deprecated updateEmail: it sends a verification
// link to the new address and only switches the account over once that link is clicked, so a
// typo'd or unowned address can never silently take over the account.
export function changeUserEmail(currentPassword: string, newEmail: string): Promise<void> {
  return reauthenticateCurrentUser(currentPassword)
    .then(user => verifyBeforeUpdateEmail(user, newEmail));
}

export function makeContainerInDB(
  parentPath: dbPath,
  typeId: ContainerType,
  content: string,
  stance: StanceType,
  url: string,
  reference: string,
  name: string,
  conditions?: string[],
  groupId?: string,
  topicIds?: string | string[],
  memberId?: string,
  answerType?: QuestionAnswerType,
) {
  const userId = getUserId();
  const path = createPathWithUUID(getLocationFromPath(parentPath), typeId);
  const topicIdsIsArray = Array.isArray(topicIds);
  switch (typeId) {
    case ContainerType.STATEMENT: {
      const statementData: _StatementData = {
        content: content,
        type: typeId,
        path: path,
        userId: memberId ? memberId : userId,
        parentPaths: [parentPath],
        childPaths: [],
        votes: {},
        creationTime: TimestampNow(),
        topics: topicIdsIsArray ? topicIds : topicIds ? [topicIds] : [],
      };
      StatementData.makeInDB(statementData, stance, groupId);
      return statementData;
      break;
    }
    case ContainerType.ARGUMENT: {
      const statementGroupData: _ArgumentData = {
        type: typeId,
        path: path,
        userId: memberId ? memberId : userId,
        parentPaths: [parentPath],
        childPaths: [],
        votes: {},
        creationTime: TimestampNow(),
        topics: topicIdsIsArray ? topicIds : topicIds ? [topicIds] : [],
      };
      ArgumentData.makeInDB(statementGroupData, stance, groupId);
      conditions.forEach((condition) => {
        const conditionPath = createPathWithUUID(getLocationFromPath(path), ContainerType.STATEMENT);
        const conditionData: _StatementData = {
          content: condition,
          type: ContainerType.STATEMENT,
          path: conditionPath,
          userId: memberId ? memberId : userId,
          parentPaths: [path],
          childPaths: [],
          votes: {},
          creationTime: TimestampNow(),
          topics: topicIdsIsArray ? topicIds : topicIds ? [topicIds] : [],
        };
        StatementData.makeInDB(conditionData, StanceType.NEUTRAL, groupId);
      });
      return statementGroupData;
      break;
    }
    case ContainerType.SOURCE: {
      const sourceData: _SourceData = {
        content: content,
        url: url,
        reference: reference,
        type: typeId,
        path: path,
        userId: memberId ? memberId : userId,
        parentPaths: [parentPath],
        childPaths: [],
        votes: {},
        creationTime: TimestampNow(),
        topics: topicIdsIsArray ? topicIds : topicIds ? [topicIds] : [],
      };
      SourceData.makeInDB(sourceData, stance, groupId);
      break;
    }
    case ContainerType.CATEGORY: {
      const categoryData: _CategoryData = {
        name: name,
        type: typeId,
        path: path,
        userId: memberId ? memberId : userId,
        parentPaths: [parentPath],
        childPaths: [],
        votes: {},
        creationTime: TimestampNow(),
        topics: [],
      };
      CategoryData.makeInDB(categoryData, stance, groupId);
      return categoryData;
      break;
    }
    case ContainerType.QUESTION: {
      const questionData: _QuestionData = {
        content: content,
        type: typeId,
        path: path,
        userId: memberId ? memberId : userId,
        parentPaths: [parentPath],
        childPaths: [],
        votes: {},
        creationTime: TimestampNow(),
        topics: topicIdsIsArray ? topicIds : topicIds ? [topicIds] : [],
        answerType: answerType ?? QuestionAnswerType.GRADATION,
      };
      QuestionData.makeInDB(questionData, stance, groupId);
      return questionData;
      break;
    }
  }
};

export async function getNumOfTutorialSlides(): Promise<number> {
  const pathToSlides = FirestoreConstants.TUTORIAL_SLIDES_PATH;
  return getCountFromServer(collection(getFirestore(), pathToSlides))
    .then(querySnapshot => {
      return querySnapshot.data().count;
    });
}

export async function getTutorialSlideData(slidePath: string): Promise<{ text: string, imageURL: string }> {
  return getDoc(doc(getFirestore(), slidePath))
    .then(doc => {
      return { text: doc.data()?.text, imageURL: doc.data()?.imageURL };
    });
}

export async function getTutorialSlideImage(imageURL: string): Promise<string> {
  return getDownloadURL(ref(getStorage(), imageURL));
}

export async function isUserMaintainer(): Promise<boolean> {
  try {
    const userId = getUserId();
    return getDoc(doc(collection(getFirestore(), FirestoreConstants.MAINTAINERS), userId))
      .then(doc => {
        // If the document exists, then the user is a maintainer
        return doc.exists() ? true : false;
      });
  } catch (error) {
    return false;
  }
}

export function onAuthStateChangedListener(callback: () => void) {
  return onAuthStateChanged(getAuth(), callback);
}

export async function updateData<T extends ContainerData>(info: ElementPath, data: {}): Promise<void> {
  return updateDoc(getRefFromDB(info), data);
}

export class Batch {
  private batch: WriteBatch;

  constructor() {
    this.batch = writeBatch(getFirestore());
  }

  public set(info: ElementPath, data: {}): Batch {
    this.batch.set(getRefFromDB(info), data);
    return this;
  }

  public update(info: ElementPath, data: {}): Batch {
    this.batch.update(getRefFromDB(info), data);
    return this;
  }

  public delete(info: ElementPath): Batch {
    this.batch.delete(getRefFromDB(info));
    return this;
  }

  public commit(): Promise<void> {
    return this.batch.commit();
  }
}

export function onContainerDataSnapshot<T extends _ContainerData>(info: ElementPath, callback: (data: T) => void, errorHandler: (error: Error) => void): () => void {
  try {
    return onSnapshot(getRefFromDB(info), doc => {
      if (doc.exists) {
        callback(doc.data() as T);
      } else {
        errorHandler(new Error('Document does not exist.'));
      }
    });
  } catch (error) {
    errorHandler(new Error('Error getting document: ' + error));
    return () => { };
  }
}

export function getFeedElementsQuery(limitCount = 10): Query {
  return _query(getFeedElementsRef(),
    where('parentPaths', '==', []),
    orderBy('creationTime', 'desc'),
    limit(limitCount))
}

export function getPublicStatementsQuery(topicId?: string, onlyMainStatements?: boolean): Query {
  if (topicId && !onlyMainStatements) {
    return _query(getFeedElementsRef(),
      where("topics", 'array-contains', topicId),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else if (topicId && onlyMainStatements) {
    return _query(getFeedElementsRef(),
      where("topics", 'array-contains', topicId),
      where('parentPaths', '==', []),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else {
    return _query(getFeedElementsRef(),
      orderBy('creationTime', 'desc'),
      limit(10))
  }
}

export function getPublicQuestionsQuery(topicId?: string): Query {
  if (topicId) {
    return _query(getPublicQuestionsReference(),
      where("topics", 'array-contains', topicId),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else {
    return _query(getPublicQuestionsReference(),
      orderBy('creationTime', 'desc'),
      limit(10))
  }
}

export function getGroupStatementsQuery(groupId: string, topicId?: string, onlyMainStatements?: boolean): Query {
  if (topicId && !onlyMainStatements) {
    return _query(getGroupStatementsReference(groupId),
      where("topics", 'array-contains', topicId),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else if (topicId && onlyMainStatements) {
    return _query(getGroupStatementsReference(groupId),
      where("topics", 'array-contains', topicId),
      where("parentPaths", '==', [`2_5-${groupId}`]),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else {
    return _query(getGroupStatementsReference(groupId),
      orderBy('creationTime', 'desc'),
      limit(10))
  }
}

export function getGroupQuestionsQuery(groupId: string, topicId?: string): Query {
  if (topicId) {
    return _query(getGroupQuestionsReference(groupId),
      where("topics", 'array-contains', topicId),
      orderBy('creationTime', 'desc'),
      limit(10))
  } else {
    return _query(getGroupQuestionsReference(groupId),
      orderBy('creationTime', 'desc'),
      limit(10))
  }
}

export function getPersonalStatementsQuery(topicId?: string): Query {
  if (topicId) {
    return _query(getUserPersonalElementsRef(),
      where('type', '==', ContainerType.STATEMENT),
      where("parentPaths", 'array-contains', topicId),
      orderBy('creationTime', 'desc'),
      limit(10)
    )
  } else {
    return _query(getUserPersonalElementsRef(),
      and(
        where('type', '==', ContainerType.STATEMENT),
        or(
          where('parentPaths', '==', [FirestoreConstants.ROOT_PATH]),
          where('parentPaths', '==', [])
        ),
      ),
      orderBy('creationTime', 'desc'),
      limit(10)
    )
  }
}

export function getPersonalTopicsQuery(): Query {
  return _query(getUserPersonalElementsRef(),
    and(
      where('type', '==', ContainerType.CATEGORY),
      or(
        where('parentPaths', '==', [FirestoreConstants.ROOT_PATH]),
        where('parentPaths', '==', [])
      ),
    ),
    orderBy('creationTime', 'desc')
  )
}

export function getPublicTopicsQuery(): Query {
  return _query(
    getPublicTopicsReference(),
    orderBy('lastAdded', 'desc'),
    limit(10)
  )
}

export function getStatementsQuery(topicId: string, groupId?: string, userId?: string, onlyMainStatements?: boolean): Query {
  if (groupId) {
    return getGroupStatementsQuery(groupId, topicId, onlyMainStatements);
  } else if (userId) {
    return getPersonalStatementsQuery()
  } else {
    return getPublicStatementsQuery(topicId, onlyMainStatements);
  }
}

export function getQuestionsQuery(topicId: string, groupId?: string, userId?: string): Query {
  if (groupId) {
    return getGroupQuestionsQuery(groupId, topicId);
  } else if (userId) {
    // TODO Enable personal questions. Not reachable from current navigation, so not a live bug.
    throw new Error('Personal questions are not implemented yet.');
  } else {
    return getPublicQuestionsQuery(topicId);
  }
}

// Firestore's `array-contains-any` accepts at most this many values per query.
const ARRAY_CONTAINS_ANY_CHUNK_SIZE = 30;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Counts how many Statements are tagged with a given topic, and how many Arguments actually
 * reply to one of those Statements -- which is the same relationship the topic's visible
 * Question/answer/argument tree uses (see `getArgumentsFromStatements`,
 * `getGroupedArgumentsForGradationQuestion`): an Argument's place under a topic is defined by
 * *who its parent is*, not by its own `topics` field.
 *
 * Counting Arguments by their own `topics` tag instead (as this used to) silently undercounts
 * any Argument whose tag doesn't match its parent Statement's -- which the tag-threading-at-
 * creation-time approach (see `makeContainerInDB`) can never fully guarantee stays in sync, since
 * the two are independent, denormalized pieces of data. Deriving the Argument count from the
 * Statements actually tagged with the topic instead makes it agree with what the topic's
 * Question -> answer -> argument view actually shows, by construction.
 *
 * Still uses Firestore's count aggregation (`getCountFromServer`) for the Argument count itself
 * (one `array-contains-any` aggregation per up-to-30 parent Statements, rather than fetching
 * every Argument), so this stays reasonably cheap; only the topic's own Statements are fetched
 * in full (to read their paths), not every Statement or Argument in the topic.
 *
 * For a group topic, Statements and Arguments live in their own `statements`/`arguments`
 * subcollections under the group (see `makeContainerInDB`/`getRefFromDB`). For a public topic,
 * both live together in the shared feed-elements collection, so they're told apart by `type` in
 * addition to `topics`.
 */
export async function getTopicElementCounts(topicId: string, groupId?: string): Promise<{ statementCount: number; argumentCount: number }> {
  const statementsQuery = groupId
    ? _query(getGroupStatementsReference(groupId), where('topics', 'array-contains', topicId))
    : _query(getFeedElementsRef(), where('topics', 'array-contains', topicId), where('type', '==', ContainerType.STATEMENT));

  const statementsSnapshot = await getDocs(statementsQuery);
  // Real Statement documents don't reliably carry a stored `path` field of their own -- it's
  // redundant with the document id (see the `_ContainerData.path` TODO), and in practice is
  // absent on older public feed documents predating that field. `ContainerData.getPath()` already
  // tolerates this by falling back to NO_PATH, but reading `.path` straight off the raw doc here
  // instead produced `undefined` entries, which made the `array-contains-any` Arguments query
  // below invalid and the whole call reject -- hiding the subheader entirely, even for a topic
  // with real tagged Statements (e.g. "Klimawandel"/climate change). Derive each path the same
  // way `getArgumentsFromStatements` already does: location + the document's own (type-included)
  // id, which is always present and doesn't depend on when the document was created.
  const statementLocation = groupId ? PathConstants.GROUP : PathConstants.PUBLIC;
  const statementPaths = statementsSnapshot.docs.map(statementDoc => `${statementLocation}_${statementDoc.id}`);

  const argumentsRef = groupId ? getGroupArgumentsReference(groupId) : getFeedElementsRef();
  const pathChunks = chunk(statementPaths, ARRAY_CONTAINS_ANY_CHUNK_SIZE);
  const argumentCounts = await Promise.all(pathChunks.map(pathChunk => {
    const argumentsQuery = groupId
      ? _query(argumentsRef, where('parentPaths', 'array-contains-any', pathChunk))
      : _query(argumentsRef, where('parentPaths', 'array-contains-any', pathChunk), where('type', '==', ContainerType.ARGUMENT));
    return getCountFromServer(argumentsQuery).then(snapshot => snapshot.data().count);
  }));

  return {
    statementCount: statementPaths.length,
    argumentCount: argumentCounts.reduce((total, count) => total + count, 0),
  };
}

export function proposeArgument(statement: string, stance: StanceType): Promise<any> {
  const proposeArgument = httpsCallable(functions, 'propose_argument');
  return proposeArgument({ statement: statement, stance: stance })
    .then((result: any) => {
      const data = result.data;
      console.log(data);
      const proposedArgument = data.proposed_argument;
      return proposedArgument;
    });
}

export function proposeTopics(statement: string): Promise<any> {
  const proposeTopics = httpsCallable(functions, 'propose_topics');
  return proposeTopics({ statement: statement })
    .then((result: any) => {
      const data = result.data;
      const proposed_topics = data.proposed_topics;
      return proposed_topics;
    });
}

export function extractStatements(): Promise<any> {
  const extractStatements = httpsCallable(functions, 'extract_statements');
  return extractStatements({ url: "https://www.tagesschau.de/inland/gesellschaft/afd-prozesse-urteile-100.html", type: "article" })
    .then((result: any) => {
      const data = result.data;
      console.log(data);
      return data;
      // const proposedArgument = data.proposed_argument;
      // return JSON.parse(proposedArgument);
    });
}

export function getRecentSourcesQuery(limitCount = 10): Query {
  return _query(getSourcesReference(),
    orderBy('creationTime', 'desc'),
    limit(limitCount))
}

// export function getSource(soureId: string): Promise<SourceData> {
//   return getDoc(doc(getSourcesReference(), soureId))
//     .then(doc => {
//       return new SourceData(doc.data() as _SourceData);
//     });
// }

export async function addGroup(groupName: string): Promise<string | undefined> {
  const newGroupReference = doc(collection(getFirestore(), FirestoreConstants.GROUPS));
  return setDoc(newGroupReference, { name: groupName, admin: getUserId() })
    .then(async () => {
      try {
        const doc = await getDoc(newGroupReference);
        return await Promise.resolve(doc.id);
      } catch (error) {
        console.error('Error getting document: ', error);
        return await Promise.resolve(undefined);
      }
    })
    .catch(error => {
      console.error('Error adding group: ', error);
      return Promise.resolve(undefined);
    });
}

export async function getAllPersonalTopics(): Promise<Object[]> {
  return getDocs(getUserPersonalElementsRef())
    .then(querySnapshot => {
      const topics: Object[] = [];
      querySnapshot.forEach(doc => {
        if (doc.data().type === ContainerType.CATEGORY) {
          console.log('Topic: ', doc.data().name);
          topics.push({ id: doc.id, name: doc.data().name });
        }
      });
      console.log('Topics: ', topics);
      return topics;
    });
}

export async function getTopic(topicId: string): Promise<string> {
  return getDoc(doc(getUserPersonalElementsRef(), topicId))
    .then(doc => {
      return doc.data().name;
    })
    .catch(error => {
      console.error('Error getting topic: ', error);
      return '';
    });
}

export async function getPersonalTopics(): Promise<Array<Object>> {
  return getDocs(getPersonalTopicsQuery())
    .then((querySnapshot) => {
      return Promise.resolve(
        querySnapshot.docs.map((document) => ({ ...document.data(), id: document.id }))
      )
    })
}

export async function getPublicTopics(): Promise<Array<Object>> {
  return getDocs(getPublicTopicsQuery())
    .then((querySnapshot) => {
      return Promise.resolve(
        querySnapshot.docs.map((document) => ({ ...document.data(), id: document.id }))
      )
    })
}

export async function getArgumentsFromStatements(statementIds: elementId[], location: PathConstants, groupId?: string): Promise<Array<ArgumentData>> {
  const collectionRef = location === PathConstants.GROUP ? getGroupArgumentsReference(groupId) : getFeedElementsRef();

  const chunks: elementId[][] = [];
  for (let i = 0; i < statementIds.length; i += 10) {
    chunks.push(statementIds.slice(i, i + 10));
  }

  const results: ArgumentData[] = [];
  for (const chunk of chunks) {
    const parentPaths = chunk.map(id => `${location}_${id}`);
    const q = _query(
      collectionRef,
      where("parentPaths", 'array-contains-any', parentPaths),
    );
    const snapshot = await getDocs(q);
    snapshot.docs.forEach(doc => {
      results.push(new ArgumentData(doc.data() as _ArgumentData, groupId));
    });
  }
  return results;
}

export type ConclusionGroupedArgument = { argument: ArgumentData; stance: StanceType };

/**
 * Like {@link getArgumentsFromStatements}, but for a gradation Question: additionally computes
 * each argument's effective conclusion stance (see computeGradationArgumentConclusionStance)
 * relative to the Question's underlying claim, so callers can merge/group arguments that share
 * the same or an opposing conclusion instead of showing them as a flat, neutral list. Only
 * meaningful for `QuestionAnswerType.GRADATION` questions -- for `ALTERNATIVE` questions, answers
 * are genuinely distinct options and should keep using the plain `getArgumentsFromStatements`.
 */
export async function getGroupedArgumentsForGradationQuestion(question: QuestionData, location: PathConstants, groupId?: string): Promise<ConclusionGroupedArgument[]> {
  const answerInfos = question.getChildInfos();
  const answerIds = answerInfos.map(info => info.id);

  const answerStanceByPath: { [answerPath: dbPath]: StanceType } = {};
  question.getChildData().forEach(entry => {
    const answerPath = Object.keys(entry)[0];
    answerStanceByPath[answerPath] = entry[answerPath];
  });

  // Each answer's own childData holds the PRO/CON arguments attached to it -- not loaded by the
  // Question listener itself, so fetch every answer once to read it.
  const answers = await Promise.all(
    answerInfos.map(info => getContainerDataFromDB<ContainerData>(info)),
  );

  const argumentToAnswerPaths: { argumentPath: dbPath; answerPath: dbPath }[] = [];
  const argumentStanceRelativeToAnswerByPath: { [argumentPath: dbPath]: StanceType } = {};
  answers.forEach((answer, index) => {
    const answerPath = convertInfoToPath(answerInfos[index]);
    (answer.getChildData() || []).forEach(entry => {
      const argumentPath = Object.keys(entry)[0];
      argumentToAnswerPaths.push({ argumentPath, answerPath });
      argumentStanceRelativeToAnswerByPath[argumentPath] = entry[argumentPath];
    });
  });

  const conclusionStanceByArgumentPath = computeGradationArgumentConclusionStances(
    argumentToAnswerPaths,
    answerStanceByPath,
    argumentStanceRelativeToAnswerByPath,
  );

  const flatArguments = await getArgumentsFromStatements(answerIds, location, groupId);

  return flatArguments
    .map(argument => ({
      argument,
      stance: conclusionStanceByArgumentPath[argument.getPath()] ?? StanceType.NEUTRAL,
    }))
    // Groups arguments with the same conclusion next to each other (SUPPORT, then OPPOSE),
    // with unmatched/legacy arguments (NEUTRAL) kept at the front rather than interleaved.
    .sort((a, b) => a.stance - b.stance);
}

/**
 * Hook for getting the list of courses, which is updated only on reload.
 *
 * @returns [courses, coursesLoading] Pair of the courses and if the courses are loading.
 */
export function useCourses(): [any[] | null, boolean] {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  useEffect(() => {
    const coursesQuery = _query(getCoursesReference());
    getDocs(coursesQuery).then((querySnapshot) => {
      setCourses(
        querySnapshot.docs.map((document) => ({ ...document.data(), id: document.id }))
      )
    }
    );
  }, []);

  return [courses, coursesLoading];
}

/**
 * Hook for getting a course, which is updated only on reload.
 *
 * @returns [course, courseLoading] Pair of the course data and whether the course is loading.
 */
export function useCourse(id: string): [any | null, boolean] {
  const [course, setCourse] = useState<any>();
  const [courseLoading, setCourseLoading] = useState(true);
  console.log("ID", id)


  useEffect(() => {
    getDoc(getCourseReference(id)).then((documentSnapshot) => {
      if (documentSnapshot.exists()) {
        setCourse({ ...documentSnapshot.data(), id: documentSnapshot.id });
      } else {
        console.log('No such document!');
      }
    }).catch((error) => {
      console.log('Error getting document:', error);
    });
  }, []);

  return [course, courseLoading];
}
